<script setup>
import { ref, computed, onMounted } from "vue";
import { Head } from "@inertiajs/inertia-vue3";
import { __ } from "@/app";
import axios from "axios";
import LayoutNew from "@/Layouts/LayoutNew.vue";
import ProgressBar from "@/Components/ProgressBar.vue";
import ReportIssue from "@/Components/ReportIssue.vue";
import Explanation from "@/Components/Explanation.vue";

const props = defineProps({
    auth: Object
});

const isPremium = computed(() => {
    if (!props.auth?.user?.premium_expires_at) return false;
    return new Date(props.auth.user.premium_expires_at) > new Date();
});

let dailyPlan = 0; // default value 20 but could be overwritten from server

const isLoading = ref(true);
const isCompletedForToday = ref(false);
const currentQuestionIndex = ref(0);
const questions = ref({});
const explanations = ref({});
const stat = ref({correct: 0, incorrect: 0, not_answered: 20});
const correctAnswer = ref("");
const currentExplanation = ref("");
const stateQuestion = ref(true);
const stateRuleDisplay = ref(false);
const error = ref('')

const loadQuiz = async (today) => {
    try {
        // const today = new Date();
        const secondsSinceMidnight = today.getHours() * 60 * 60 + today.getMinutes() * 60 + today.getSeconds();
        const result = await axios.get(route("quiz.questions"), {
            params: { sec: secondsSinceMidnight },
        });
        
        explanations.value = result.data.explanations;
        questions.value = result.data.questions;
        stat.value = result.data.stat;
        dailyPlan = result.data.stat.daily_plan;
        
        isCompletedForToday.value = isPremium.value ? false : result.data.stat.daily_plan <= result.data.stat.correct + result.data.stat.incorrect;        
    } catch (err) {
        error.value = "Failed to load quiz. Please try again.";
        console.error("Quiz loading error:", err);
    } finally {
        isLoading.value = false;
    }
};

const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
            const registration = await navigator.serviceWorker.register('/service-worker.js');
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: process.env.MIX_VAPID_PUBLIC_KEY
            });
            
            await axios.post(route('notifications.update-subscription'), {
                subscription: JSON.stringify(subscription)
            });
        } catch (error) {
            console.error('Error registering service worker:', error);
        }
    }
};

onMounted(() => {
    loadQuiz(new Date());
    registerServiceWorker();
});

// utils
function replaceBracesWithUnderscores(text, msg) {
    return text.replace(/\(.*?\)/g, msg);
}

function extractStringsFromString(str) {
    const regex = /\(([^)]+)\)/;
    const match = str.match(regex);

    if (match) {
        const strings = match[1].split("/");
        strings.sort(() => Math.random())        
        return [...strings].sort(() => Math.random() - 0.5); // sort in random order answers options
    } else {
        return [];
    }
}

const getCurrentQuestion = () => questions && questions.value[currentQuestionIndex.value];
const getCurrentExplanation = () => {
    if (!explanations.value || !getCurrentQuestion() || !explanations.value[getCurrentQuestion().exercise_type_id]) {
        return null;
    }

    return explanations.value[getCurrentQuestion().exercise_type_id]
};
const currentQuestionText = computed(() =>
    getCurrentQuestion() && replaceBracesWithUnderscores(getCurrentQuestion().question, "___")
);
const answers = computed(() =>
    getCurrentQuestion() && extractStringsFromString(getCurrentQuestion().question)
);
const completed = () => parseInt(stat.value.correct) + parseInt(stat.value.incorrect);

const progressPercent = computed(() => {    
    return completed() > dailyPlan ? 100 : Math.round(completed() / dailyPlan * 100)
});

// logic
const checkAnswer = async (n) => {
    let answerHtml;
    const currentExercise = getCurrentQuestion();
    currentExplanation.value = getCurrentExplanation() || '';
    const answer = currentExercise.answer;
    const isCorrect = n.toLowerCase() == answer.toLowerCase();
    if (isCorrect) {
        stat.value.correct++;
        answerHtml = `<span class="correct">${answer}</span>`;
    } else {
        stat.value.incorrect++;
        answerHtml = `<span class="incorrect">${n}</span> <span class="correct">${answer}</span>`;
    }

    stat.value.not_answered--;

    correctAnswer.value = replaceBracesWithUnderscores(
        currentExercise.question,
        answerHtml
    );
    stateQuestion.value = false;
    axios.post(route("quiz.answer"), {
        quiz_exercise_id: currentExercise.quiz_exercise_id,
        answer: n
    });
    
    if(questions.value.length <= currentQuestionIndex.value + 1) {        
        await loadQuiz(new Date());
        currentQuestionIndex.value = -1;
    }
};

const nextQuestion = () => {
    stateQuestion.value = true;
    stateRuleDisplay.value = false;
    currentQuestionIndex.value++;    
    isCompletedForToday.value = isPremium.value ? false : progressPercent.value >= 100;
};

const whyIsThat = () => {
    stateRuleDisplay.value = true;
}
</script>
<template>
    <LayoutNew layout="vertical">
        <Head title="Quizz" />
        
        <div v-if="isLoading" class="row">Loading quiz...</div>
        <div class="row my-5" v-if="!isLoading">
            <!-- Sidebar -->
            <div class="col-md-4">
                <h4 class="mb-4" v-text="__('Twój progres')" />
                <div class="progress mb-4">
                    <ProgressBar :progress="progressPercent" :key="progressPercent" />
                </div>
                <ul class="list-group mb-4 d-none d-md-block">
                    <li class="list-group-item">
                        {{ __("Cel na dziś") }}: {{ progressPercent }}%
                    </li>                        
                    <li class="list-group-item">
                        {{ __("Na dziś zostało") }}:
                        {{ dailyPlan - completed() > 0 ? (dailyPlan - completed()) : 0 }}
                    </li>
                    <li class="list-group-item">
                        {{ __("Na dziś zrobione") }}:
                        {{ completed() }}
                    </li>
                    <li class="list-group-item">
                        {{ __("Poprawne odpowiedzi") }}: {{ stat.correct }}
                    </li>
                    <li class="list-group-item">
                        {{ __("Błędne odpowiedzi") }}:
                        {{ stat.incorrect }}
                    </li>
                </ul>
            </div>

            <!-- Flashcard Section -->
            <div class="col-md-8">
                <h4 class="mb-4" v-text="__('Ćwiczenie')" />                
                <div class="flashcard mb-4 m-1 row">
                    <div class="col-12 text-center pt-3">
                        <div class="alert alert-danger" v-if="error">{{ error }}</div>
                        <p v-if="isCompletedForToday">{{ __('Brawo! Zrobiłeś wszystko na dziśaj!') }}</p>
                        <p v-if="!isCompletedForToday && stateQuestion">{{ currentQuestionText }}</p>
                        <p v-if="!isCompletedForToday && !stateQuestion" v-html="correctAnswer"></p>
                        <Explanation v-if="!isCompletedForToday && stateRuleDisplay" :html-content="currentExplanation"/>
                        <ReportIssue v-if="!isCompletedForToday && stateRuleDisplay" :question="getCurrentQuestion()"/>
                    </div>
                </div>
                <div class="text-center" v-if="!isCompletedForToday">
                    <button
                        v-if="stateQuestion"
                        class="btn btn-primary btn-lg mb-2 me-2"
                        v-for="answer in answers"
                        :key="answer"
                        @click="checkAnswer(answer)"
                    >
                        {{ answer }}
                    </button>
                    <button
                        v-if="!stateQuestion && currentExplanation != ''"
                        class="btn btn-success btn-lg mb-2 me-2"
                        @click="whyIsThat()"
                    >
                        {{ __("Dlaczego tak?") }}
                    </button>
                    <button
                        v-if="!stateQuestion"
                        class="btn btn-primary btn-lg mb-2 me-2"
                        @click="nextQuestion()"
                    >
                        {{ __("Dalej") }}
                        <i class="bi bi-arrow-right"></i>
                    </button>      
                </div>
            </div>
        </div>
        
    </LayoutNew>
</template>
<style>
.flashcard {    
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    border-radius: 8px;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.05);
    background-color: var(--bs-dark-bg);
    border: 1px solid var(--bs-border-color);
    
}
.incorrect {
    color: var(--bs-danger);
    font-weight: bold;
    text-decoration: line-through;
}
.correct {
    color: var(--bs-success);
    font-weight: bold;
}
</style>